import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import type {
  GHADiscountExclusion,
  GHADiscountExclusionBody,
  GHADiscountExclusionPatchBody,
  GHADiscountRuleAccountType,
  GHAScopeRuleCugTier,
} from '~/services/ConnectionGoogleService';

import ScopeDimensionFields from './ScopeDimensionFields';
import { GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS } from './constants';
import { normaliseScopeDimensions } from './utils';

interface Props {
  open: boolean;
  /** Present = in-place edit (exclusions mint no tokens, so they are freely mutable). */
  initialRule?: GHADiscountExclusion | null;
  isSaving: boolean;
  submitError?: string | null;
  onClose: () => void;
  onCreate: (input: GHADiscountExclusionBody) => void;
  onUpdate: (id: string, input: GHADiscountExclusionPatchBody) => void;
}

// Lists are OR within a dimension, AND across dimensions; empty list = match everything on it.
interface FormState {
  accountType: GHADiscountRuleAccountType;
  suppliers: Array<string>;
  chainNames: Array<string>;
  cugTiers: Array<GHAScopeRuleCugTier>;
  userCountries: Array<string>;
}

const toFormState = (rule: GHADiscountExclusion | null | undefined): FormState => ({
  accountType: rule?.accountType ?? 'lpp',
  suppliers: rule?.suppliers ?? [],
  chainNames: rule?.chainNames ?? [],
  cugTiers: rule?.cugTiers ?? [],
  userCountries: rule?.userCountries ?? [],
});

export default function ExclusionDialog({
  open,
  initialRule,
  isSaving,
  submitError,
  onClose,
  onCreate,
  onUpdate,
}: Props) {
  const [form, setForm] = useState<FormState>(() => toFormState(initialRule));
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(toFormState(initialRule));
      setValidationError(null);
    }
  }, [open, initialRule]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = () => {
    const dims = normaliseScopeDimensions(form);
    if (dims.error != null) {
      setValidationError(dims.error);
      return;
    }
    setValidationError(null);
    if (initialRule == null) {
      onCreate({ accountType: form.accountType, ...dims });
    } else {
      // PATCH semantics: explicit null clears a dimension back to match-all.
      onUpdate(initialRule.id, {
        suppliers: dims.suppliers ?? null,
        chainNames: dims.chainNames ?? null,
        cugTiers: dims.cugTiers ?? null,
        userCountries: dims.userCountries ?? null,
      });
    }
  };

  const isEdit = initialRule != null;
  const error = validationError ?? submitError;

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit exclusion' : 'New exclusion'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            An exclusion vetoes every matching leg unconditionally — order-free, before any grant is considered. Pick
            several values to veto any of them; leave a dimension empty to match everything on it.
          </Typography>

          <FormControl size="small" fullWidth>
            <InputLabel id="exclusion-account">Account</InputLabel>
            <Select
              labelId="exclusion-account"
              label="Account"
              value={form.accountType}
              disabled={isEdit}
              onChange={(event) => set('accountType', event.target.value)}
            >
              {GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ScopeDimensionFields
            idPrefix="exclusion"
            value={form}
            onChange={set}
            countriesEmptyHelper="Empty = every country"
          />

          {error != null && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" color="warning" onClick={handleSubmit} disabled={isSaving}>
          {isEdit ? 'Save' : 'Create exclusion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
